import React from 'react'
import { Tooltip } from '@mui/material'
import { DeleteOutline, SaveOutlined, CheckCircleRounded, WarningAmberRounded } from '@mui/icons-material'
import { api } from '../../function/axios'
import { ConfirmV2 } from '../ConfirmV2'
import s from './SystemV2.module.css'

/*
 * PDF report prompts — the instructions the AI follows when it writes a PDF
 * report, one per country. The classic panel's calls
 * (page/system_page/PdfReportPrompt.tsx): GET /admin_settings/pdf_report,
 * POST it ({id, name: the country code, content}), DELETE …/:id. The search
 * picks the prompt whose name is the report's country code
 * (SearcherService) — with none, the report is written with no instructions,
 * which the list now says. A prompt under any other name is shown too.
 */

type Row = { id?: number; code: string; text: string; updated?: string }

const COUNTRIES: { code: string; label: string; rtl?: boolean }[] = [
  { code: 'US', label: 'United States' },
  { code: 'IL', label: 'Israel', rtl: true },
]
const flag = (code: string) => (/^[A-Z]{2}$/.test(code) ? String.fromCodePoint(...Array.from(code).map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : '🏳')
const labelOf = (code: string) => COUNTRIES.find((c) => c.code === code)?.label || code
const when = (iso?: string) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '')
const errText = (e: any) => (typeof e?.response?.data?.message === 'string' ? e.response.data.message : e?.message) || 'Something went wrong'

export function PdfPromptsV2() {
  const [rows, setRows] = React.useState<Row[] | null>(null)
  const [sel, setSel] = React.useState<string>(COUNTRIES[0].code)
  const [draft, setDraft] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [note, setNote] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const [toDelete, setToDelete] = React.useState<Row | null>(null)
  // unsaved changes would be lost — asked first (ConfirmV2)
  const [leaveFor, setLeaveFor] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    const { data } = await api({ method: 'GET', url: '/admin_settings/pdf_report' })
    const list: Row[] = (Array.isArray(data) ? data : []).map((d: any) => ({
      id: Number.isFinite(Number(d.id)) ? Number(d.id) : undefined,
      // the classic panel read the code after the last "_" — the search compares the whole name
      code: String(d.name || ''),
      text: d.content ?? '',
      updated: d.updated_at || undefined,
    }))
    setRows(list)
    return list
  }, [])

  React.useEffect(() => {
    load().then((list) => setDraft(list.find((r) => r.code === COUNTRIES[0].code)?.text ?? '')).catch((e) => { setRows([]); setNote({ kind: 'err', text: errText(e) }) })
  }, [load])

  const saved = rows?.find((r) => r.code === sel)
  const dirty = draft !== (saved?.text ?? '')
  const others = (rows || []).filter((r) => !COUNTRIES.some((c) => c.code === r.code))
  const codes = [...COUNTRIES.map((c) => c.code), ...others.map((r) => r.code)]
  const rtl = !!COUNTRIES.find((c) => c.code === sel)?.rtl

  function pick(code: string, sure = false) {
    if (code === sel) return
    if (dirty && !sure) { setLeaveFor(code); return }
    setSel(code)
    setDraft(rows?.find((r) => r.code === code)?.text ?? '')
    setNote(null)
  }

  async function save() {
    if (!draft.trim()) { setNote({ kind: 'err', text: 'The prompt is empty — delete it instead if the country should have none' }); return }
    setSaving(true)
    setNote(null)
    try {
      await api({ method: 'POST', url: '/admin_settings/pdf_report', body: { id: saved?.id, name: sel, content: draft } })
      const list = await load()
      setDraft(list.find((r) => r.code === sel)?.text ?? draft)
      setNote({ kind: 'ok', text: `Saved — ${labelOf(sel)} reports use it now` })
    } catch (e) {
      setNote({ kind: 'err', text: errText(e) })
    } finally {
      setSaving(false)
    }
  }

  async function remove(r: Row) {
    try {
      await api({ method: 'DELETE', url: `/admin_settings/pdf_report/${r.id}` })
      await load()
      if (r.code === sel) setDraft('')
      setNote({ kind: 'ok', text: `Deleted — ${labelOf(r.code)} reports have no prompt now` })
    } catch (e) {
      setNote({ kind: 'err', text: errText(e) })
    }
  }

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <div>
          <b>PDF report prompts</b>
          <span>The instructions the AI follows when it writes a PDF report — one per country.</span>
        </div>
      </div>

      <div className={s.card}>
        {rows === null ? <div className={s.hint}>Loading…</div> : (
          <div className={s.prompts}>
            <div className={s.promptList}>
              {codes.map((code) => {
                const r = rows.find((x) => x.code === code)
                return (
                  <button key={code} type="button" className={`${s.promptItem} ${code === sel ? s.promptOn : ''}`} onClick={() => pick(code)}>
                    <b><span className={s.flag}>{flag(code)}</span>{labelOf(code)}<em className={s.codeTag}>{code}</em></b>
                    {r?.text
                      ? <span>{r.updated ? `Updated ${when(r.updated)}` : 'Set'}</span>
                      : <span className={s.missing}><WarningAmberRounded sx={{ fontSize: 12 }} />No prompt — reports get no instructions</span>}
                  </button>
                )
              })}
            </div>

            <div className={s.editor}>
              <div className={s.editorTop}>
                <span className={s.editorTitle}><span className={s.flag}>{flag(sel)}</span>{labelOf(sel)}</span>
                {rtl && <span className={s.muted}>written right to left</span>}
                <span className={s.spacer} />
                {saved?.text && <span className={s.activeBig}><CheckCircleRounded sx={{ fontSize: 15 }} />In use</span>}
              </div>
              <textarea
                className={s.promptText}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                dir={rtl ? 'rtl' : 'ltr'}
                placeholder={`How the AI should write ${labelOf(sel)} reports…`}
                spellCheck={false}
              />
              <div className={s.editorBar}>
                <span className={s.muted}>{draft.length.toLocaleString()} characters{dirty ? ' · not saved' : ''}</span>
                <span className={s.spacer} />
                {saved?.id != null && (
                  <Tooltip title="Delete this prompt"><button type="button" className={s.iconDanger} onClick={() => setToDelete(saved)} aria-label="Delete"><DeleteOutline sx={{ fontSize: 17 }} /></button></Tooltip>
                )}
                {dirty && <button type="button" className={s.ghostBtn} onClick={() => setDraft(saved?.text ?? '')} disabled={saving}>Undo</button>}
                <button type="button" className={s.primary} onClick={() => { void save() }} disabled={saving || !dirty}><SaveOutlined sx={{ fontSize: 16 }} />{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </div>
        )}
        {note && (
          <div className={`${s.notice} ${note.kind === 'err' ? s.noticeErr : s.noticeOk}`}>
            {note.kind === 'ok' ? <CheckCircleRounded sx={{ fontSize: 16 }} /> : <WarningAmberRounded sx={{ fontSize: 16 }} />}
            <span>{note.text}</span>
            <button type="button" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
          </div>
        )}
      </div>

      {toDelete && (
        <ConfirmV2
          header={`Delete the ${labelOf(toDelete.code)} prompt? Its reports will be written with no instructions.`}
          onYes={() => { void remove(toDelete) }}
          onClose={() => setToDelete(null)}
        />
      )}
      {leaveFor !== null && (
        <ConfirmV2 header="Leave this prompt? Your changes are not saved." onYes={() => pick(leaveFor, true)} onClose={() => setLeaveFor(null)} />
      )}
    </div>
  )
}
